/**
 * Forgot password flow component
 *
 * Three-step inline state machine: email entry, verification code + new password,
 * and success confirmation. Extracted from LoginPage to keep that file focused
 * on the primary login/register form.
 */

import { CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** Steps in the forgot password flow */
type ForgotPasswordStep = 'email' | 'code' | 'done';

/** Props for the ForgotPasswordFlow component */
interface ForgotPasswordFlowProps {
  /** Send a verification code to the given email */
  forgotPassword: (email: string) => Promise<void>;
  /** Confirm the password reset with code and new password */
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Called when user navigates back to the login form */
  onBackToLogin: () => void;
}

/**
 * Renders the forgot password flow as three sequential steps:
 * 1. Email entry — sends a verification code
 * 2. Code + new password entry — confirms the reset
 * 3. Success message with link back to sign in
 */
export function ForgotPasswordFlow({
  forgotPassword,
  confirmForgotPassword,
  onBackToLogin
}: ForgotPasswordFlowProps) {
  const [forgotStep, setForgotStep] = useState<ForgotPasswordStep>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  /** Send the reset code to the entered email */
  async function handleForgotSendCode() {
    setForgotError(null);
    setForgotLoading(true);
    try {
      await forgotPassword(forgotEmail);
      setForgotStep('code');
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : 'Failed to send reset code');
    } finally {
      setForgotLoading(false);
    }
  }

  /** Confirm the password reset with the verification code */
  async function handleForgotConfirm() {
    setForgotError(null);
    setForgotLoading(true);
    try {
      await confirmForgotPassword(forgotEmail, resetCode, newPassword);
      setForgotStep('done');
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  }

  /** Reset internal state and notify parent */
  function handleBackToLogin() {
    setForgotStep('email');
    setForgotEmail('');
    setResetCode('');
    setNewPassword('');
    setForgotError(null);
    onBackToLogin();
  }

  if (forgotStep === 'email') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Enter your email and we'll send you a verification code to reset your password.
        </p>
        {forgotError && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-md p-2">
            {forgotError}
          </p>
        )}
        <div className="space-y-1.5">
          <label htmlFor="forgot-email" className="text-sm font-medium">
            Email
          </label>
          <Input
            id="forgot-email"
            type="email"
            placeholder="you@example.com"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            autoFocus
          />
        </div>
        <Button
          type="button"
          variant="accent"
          className="w-full"
          onClick={handleForgotSendCode}
          disabled={forgotLoading || !forgotEmail}
        >
          {forgotLoading ? 'Sending...' : 'Send Reset Code'}
        </Button>
        <button
          type="button"
          onClick={handleBackToLogin}
          className="block w-full text-center text-sm text-primary hover:underline"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  if (forgotStep === 'code') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          We sent a verification code to <strong>{forgotEmail}</strong>. Enter it below with your new password.
        </p>
        {forgotError && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-md p-2">
            {forgotError}
          </p>
        )}
        <div className="space-y-1.5">
          <label htmlFor="reset-code" className="text-sm font-medium">
            Verification Code
          </label>
          <Input
            id="reset-code"
            placeholder="Enter code"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="new-password" className="text-sm font-medium">
            New Password
          </label>
          <Input
            id="new-password"
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Must be at least 8 characters
          </p>
        </div>
        <Button
          type="button"
          variant="accent"
          className="w-full"
          onClick={handleForgotConfirm}
          disabled={forgotLoading || !resetCode || !newPassword}
        >
          {forgotLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
        <button
          type="button"
          onClick={handleBackToLogin}
          className="block w-full text-center text-sm text-primary hover:underline"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  // forgotStep === 'done'
  return (
    <div className="space-y-4 text-center">
      <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
      <p className="text-sm text-foreground font-medium">
        Password reset successfully
      </p>
      <p className="text-sm text-muted-foreground">
        You can now sign in with your new password.
      </p>
      <button
        type="button"
        onClick={handleBackToLogin}
        className="text-sm text-primary hover:underline font-medium"
      >
        Back to sign in
      </button>
    </div>
  );
}
